import type { FunctionComponent, ReactNode } from 'react';
import { Box, Tooltip } from '@mantine/core';

import { useTranslation } from 'i18n';

const NS = 'menuManagementEditDrawer';

type PosSyncLockedFieldProps = {
  children: ReactNode;
};

/* Wraps an edit-drawer field that a POS sync-in-place menu owns (CN-50652): the value stays
visible and readable but cannot be changed, and hovering or focusing explains why. The tooltip
target keeps its own pointer events and a tab stop so the notice is reachable by mouse and
keyboard. The content below it is neutralized two ways:
- `fieldset disabled` natively disables every form control inside, which is also what takes
  them out of the tab order. Every widget the drawer can render here is a form control.
- `pointer-events: none` stops mouse interaction reaching anything the fieldset misses.
Deliberately NOT `inert`: it would remove the subtree from the accessibility tree, hiding the
very POS values this component exists to keep readable. No extra dimming either - the fieldset
puts each control into its native :disabled state, which the design system already styles. */
export const PosSyncLockedField: FunctionComponent<PosSyncLockedFieldProps> = ({ children }) => {
  const { t } = useTranslation(NS);

  return (
    <Tooltip
      label={t('PosSyncLockedField.notice')}
      position="top"
      w="auto"
      maw="240px"
      multiline
      events={{ hover: true, focus: true, touch: true }}
    >
      <Box
        role="group"
        data-testid="pos-sync-locked-field"
        tabIndex={0}
        style={{ cursor: 'not-allowed' }}
      >
        <Box
          component="fieldset"
          disabled
          data-testid="pos-sync-locked-field-content"
          bd="none"
          p={0}
          m={0}
          style={{ pointerEvents: 'none', minInlineSize: 'auto' }}
        >
          {children}
        </Box>
      </Box>
    </Tooltip>
  );
};
